import React, { useCallback, useEffect, useContext } from 'react';
import { HeaderCellButton } from './HeaderCellButton';
import { Popover, usePopover, PopoverScreen } from '@trello/nachos/popover';
import { forTemplate } from '@trello/i18n';
import { DueFilterPopoverContent } from './filters/DueFilterPopoverContent';

import { RangeFilter, CompleteFilter } from './filters/DueFilter';
import { SavedViewContext } from './SavedViewContext';
import { Analytics } from '@trello/atlassian-analytics';

const format = forTemplate('multi_board_table_view');

enum Screen {
  DueFilterScreen,
}

interface DueHeaderCellProps {
  toggleSortBy: (desc: boolean, multi: boolean) => void;
  clearSortBy: () => void;
  isSorted: boolean;
  isSortedDesc?: boolean;
  idOrg: string;
  setServerSortBy: (sortBy: string | null) => void;
}

export const DueHeaderCell: React.FunctionComponent<DueHeaderCellProps> = ({
  toggleSortBy,
  setServerSortBy,
  clearSortBy,
  isSorted,
  isSortedDesc,
  idOrg,
}) => {
  const { toggle, triggerRef, popoverProps } = usePopover<HTMLDivElement>({
    initialScreen: Screen.DueFilterScreen,
  });

  useEffect(() => {
    if (popoverProps.isVisible) {
      Analytics.sendScreenEvent({
        name: 'dueDateFilterInlineDialog',
        containers: {
          organization: {
            id: idOrg,
          },
        },
      });
    }
  }, [popoverProps.isVisible, idOrg]);
  const { savedView } = useContext(SavedViewContext);

  const onClearSorting = useCallback(() => {
    clearSortBy();
    setServerSortBy(null);
  }, [clearSortBy, setServerSortBy]);

  const onToggleSorting = useCallback(
    (desc: boolean, multi: boolean) => {
      setServerSortBy(desc ? '-due' : 'due');
      toggleSortBy(desc, multi);
    },
    [toggleSortBy, setServerSortBy],
  );

  const onToggleDuePopover = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      source: 'multiBoardTableViewScreen',
      buttonName: 'filterTableByDueDateButton',
      containers: {
        organization: {
          id: idOrg,
        },
      },
    });

    toggle();
  }, [toggle, idOrg]);

  return (
    <>
      <HeaderCellButton
        heading={format('due-date')}
        onClick={onToggleDuePopover}
        isClickable
        isFilterActive={
          savedView.filters.due.rangeFilter !== RangeFilter.None ||
          savedView.filters.due.completeFilter !== CompleteFilter.None ||
          savedView.filters.due.overdue
        }
        isSortingActive={isSorted}
        isSortedDesc={isSortedDesc}
        forwardedRef={triggerRef}
        isActive={popoverProps.isVisible}
      />
      <Popover
        title={format('due-date')}
        {...popoverProps}
        dangerous_disableHideOnUrlSearchParamsChange
        dontOverlapAnchorElement
      >
        <PopoverScreen id={Screen.DueFilterScreen}>
          <DueFilterPopoverContent
            idOrg={idOrg}
            popoverTarget={'headerCell'}
            showSortingOptions={true}
            showResetButton={true}
            isSorted={isSorted}
            isSortedDesc={isSortedDesc}
            savedView={savedView}
            onToggleSorting={onToggleSorting}
            onClearSorting={onClearSorting}
            onClearDueFilter={onClearSorting}
          ></DueFilterPopoverContent>
        </PopoverScreen>
      </Popover>
    </>
  );
};
